// src/utils/billTo.js
// billto-mirror-4f8a2c6e -- HAND-SYNCED MIRROR of functions/utils/billTo.js.
// Any change here must be copied there in the same commit (and vice versa).
//
// Canonical bill-to block for quotes, jobs, and invoices.
//   billTo == null (or the field absent)  ->  bill the MAIN customer (today's behaviour).
//   billTo == { mode: 'propertyContact' | 'thirdParty', ... }  ->  bill someone else.
// A block is built ONLY by the functions below; no writer may hand-assemble one.
// The address is stored as DISCRETE fields, never a joined string.

export const BILLTO_MODES = {
  PROPERTY_CONTACT: 'propertyContact',
  THIRD_PARTY: 'thirdParty',
};

const STRING_KEYS = ['name', 'company', 'email', 'phone', 'address1', 'address2', 'city', 'state', 'zip', 'country'];

const str = (v) => (typeof v === 'string' ? v.trim() : '');

// Mode 'propertyContact': bill the selected property's site contact. Contact
// fields fall back to the main customer's when blank (the property editor
// promises exactly this convention). The address is the property's own.
export function buildBillToFromProperty(property, customer) {
  if (!property || !property.id) return null;
  const c = customer || {};
  return {
    mode: BILLTO_MODES.PROPERTY_CONTACT,
    customerId: null,
    propertyId: property.id,
    name: str(property.contactName) || str(c.name),
    company: '',
    email: str(property.contactEmail) || str(c.email),
    phone: str(property.contactPhone) || str(c.phone),
    address1: str(property.address1),
    address2: str(property.address2),
    city: str(property.city),
    state: str(property.state),
    zip: str(property.zip),
    country: str(property.country),
  };
}

// Mode 'thirdParty': bill another existing customer (property manager,
// insurer, ...). Reference (customerId) plus snapshot -- both, always.
export function buildBillToFromCustomer(thirdPartyCustomer) {
  if (!thirdPartyCustomer || !thirdPartyCustomer.id) return null;
  const c = thirdPartyCustomer;
  return {
    mode: BILLTO_MODES.THIRD_PARTY,
    customerId: c.id,
    propertyId: null,
    name: str(c.name),
    company: str(c.company),
    email: str(c.email),
    phone: str(c.phone),
    address1: str(c.address1),
    address2: str(c.address2),
    city: str(c.city),
    state: str(c.state),
    zip: str(c.zip),
    country: str(c.country),
  };
}

// Copy-through sanitizer. Returns null for anything that is not a valid block;
// otherwise returns a NEW object with exactly the canonical keys. Every
// conversion/backend writer passes billTo through this, so a malformed client
// block can never propagate a new shape.
export function normalizeBillTo(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (raw.mode !== BILLTO_MODES.PROPERTY_CONTACT && raw.mode !== BILLTO_MODES.THIRD_PARTY) return null;
  const out = {
    mode: raw.mode,
    customerId: typeof raw.customerId === 'string' && raw.customerId ? raw.customerId : null,
    propertyId: typeof raw.propertyId === 'string' && raw.propertyId ? raw.propertyId : null,
  };
  STRING_KEYS.forEach((k) => { out[k] = str(raw[k]); });
  if (out.mode === BILLTO_MODES.THIRD_PARTY && !out.customerId) return null;
  if (out.mode === BILLTO_MODES.PROPERTY_CONTACT && !out.propertyId) return null;
  return out;
}
